import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { ContentCollectionEdge, getContentCollections } from '@/services/CollectionsService'
import { getTheatricalReleasesWithCritics, TheatricalCriticsData } from '@/services/Theatrical'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { getManualFilmData } from './data'

/**
 * Film data for the Theatrical Portrait Rail
 */
export interface TheatricalFilm {
  id: string
  title: string
  posterUrl: string
  slug: string
  isAngelOriginal?: boolean
  guildScore?: number | null
  rottenTomatoesCritics?: number | null
  rottenTomatoesAudience?: number | null
  releaseDate?: string | null
}

/**
 * Maps a collection edge to a TheatricalFilm, augmented with critics data and manual overrides
 */
export function mapEdgeToFilm(
  edge: ContentCollectionEdge,
  criticsMap: Map<string, TheatricalCriticsData>,
): TheatricalFilm | null {
  if (!edge?.node) return null

  const node = edge.node
  const image = node?.portraitAngelImage?.cloudinaryPath ?? node?.posterTitleImage?.cloudinaryPath
  if (!image) return null

  const slug = ('project' in node && node?.project?.slug) || ''
  const criticsData = slug ? criticsMap.get(slug) : undefined
  const manualData = slug ? getManualFilmData(slug) : undefined
  const hasTheatricalRelease = Boolean(criticsData)

  return {
    id: edge?.node?.id ?? '',
    title: node.name ?? '',
    posterUrl: image,
    slug,
    isAngelOriginal: hasTheatricalRelease,
    guildScore: node?.project?.highestScore,
    // For Angel Originals, use API data; for others, use manual data
    rottenTomatoesCritics: criticsData?.rottenTomatoesCritics ?? manualData?.rottenTomatoesCritics ?? null,
    rottenTomatoesAudience: criticsData?.rottenTomatoesAudience ?? manualData?.rottenTomatoesAudience ?? null,
    releaseDate: manualData?.releaseDate ?? node?.project?.releaseDate,
  }
}

/**
 * Fetches theatrical films for a given collection ID.
 * This can be called server-side to pre-fetch data for the TheatricalPortraitRail component.
 *
 * @param collectionId - The ID of the content collection to fetch films from
 * @param client - Apollo client instance
 * @returns Array of TheatricalFilm objects, or null if the fetch fails
 */
export async function fetchTheatricalReleasesCollection(
  collectionId: string,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<TheatricalFilm[] | null> {
  try {
    const [collectionResponse, criticsMap] = await Promise.all([
      getContentCollections([collectionId], client),
      getTheatricalReleasesWithCritics(client),
    ])

    const edges = collectionResponse?.[0]?.connection?.edges?.filter(isDefined) ?? []
    const mappedFilms = edges.map((edge) => mapEdgeToFilm(edge, criticsMap)).filter(isDefined)

    return mappedFilms
  } catch (error) {
    logger().warn('Error fetching theatrical films server-side', { collectionId }, error)
    return null
  }
}
